import React, { useState } from 'react';
import { ArrowRight, Loader2, Plus, X } from 'lucide-react';
import { useFieldArray } from 'react-hook-form';
import { z } from 'zod';
import {
  Form,
  FormField,
  Input,
  applyServerErrors,
  useZodForm,
} from '@relay-towing/ui';
import { useSendMemberInvitation } from '@/api/hooks/useSendMemberInvitation';
import { useDriverStateV2 } from '@/hooks/useDriverStateV2';
import { useToast } from '@/contexts/ToastContext';
import OnboardingStepChrome from './OnboardingStepChrome';
import { STEPS } from './onboardingSteps';

interface InviteDispatchersOnboardingScreenProps {
  onBack: () => void;
  onSuccess: () => void;
  onSkip: () => void;
}

/**
 * Every row is validated in full, so a half-filled one names its own missing
 * fields rather than raising the single "complete or clear incomplete rows"
 * toast this screen used to show for any of them.
 */
const dispatcherRowSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .min(2, 'Name must be at least 2 characters'),
  email: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
});

const inviteDispatchersSchema = z.object({
  dispatchers: z
    .array(dispatcherRowSchema)
    .min(1, 'Add at least one dispatcher.'),
});

type InviteDispatchersFormData = z.infer<typeof inviteDispatchersSchema>;

const emptyRow = () => ({ name: '', email: '' });

const InviteDispatchersOnboardingScreen: React.FC<
  InviteDispatchersOnboardingScreenProps
> = ({ onBack, onSuccess, onSkip }) => {
  const { showToast } = useToast();
  const { driver } = useDriverStateV2();
  const { mutateAsync: sendInvitation } = useSendMemberInvitation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useZodForm(inviteDispatchersSchema, {
    defaultValues: { dispatchers: [emptyRow()] },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'dispatchers',
  });

  const onSubmit = async (data: InviteDispatchersFormData) => {
    if (!driver?.companyId) {
      showToast('error', 'No company ID available. Please try again.');
      return;
    }

    const rows = data.dispatchers;

    setIsSubmitting(true);
    try {
      for (const row of rows) {
        await sendInvitation({
          email: row.email.trim(),
          inviteeName: row.name.trim(),
          officeRole: 'DISPATCH',
        });
      }
      showToast(
        'success',
        rows.length === 1
          ? 'Invitation sent successfully!'
          : `${rows.length} invitations sent successfully!`
      );
      onSuccess();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      applyServerErrors(form, err, `Failed to send invitation: ${message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}
      className="flex flex-col"
    >
      <OnboardingStepChrome
        filledSegmentCount={3}
        segmentCount={STEPS.length}
        title="Add Dispatchers"
        subtitle="Invite admins"
        onBack={onBack}
      />

      <Form
        form={form}
        gap={0}
        onSubmit={onSubmit}
        className="flex flex-col flex-1"
      >
        <div className="flex-1 px-4 py-5 space-y-4 overflow-y-auto">
          <p className="text-sm" style={{ color: '#6B7280' }}>
            Invite team members who will use the Relay admin web app for
            dispatch and company settings. They accept the invite from the link
            in their email.
          </p>

          {fields.map((field, index) => (
            <div
              key={field.id}
              className="rounded-xl border p-4 space-y-3"
              style={{ borderColor: '#E5E7EB' }}
            >
              {index > 0 && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="p-1 rounded-lg"
                    style={{ color: '#6B7280' }}
                    aria-label="Remove this dispatcher"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}

              <FormField
                form={form}
                name={`dispatchers.${index}.name`}
                label="Name"
                size="sm"
              >
                {(bound) => (
                  <Input
                    {...bound}
                    size="sm"
                    placeholder="Full name"
                    autoComplete="name"
                  />
                )}
              </FormField>

              <FormField
                form={form}
                name={`dispatchers.${index}.email`}
                label="Email"
                size="sm"
              >
                {(bound) => (
                  <Input
                    {...bound}
                    size="sm"
                    type="email"
                    placeholder="name@company.com"
                    autoComplete="email"
                  />
                )}
              </FormField>
            </div>
          ))}

          <button
            type="button"
            onClick={() => append(emptyRow())}
            className="w-full py-3 rounded-xl border-2 border-dashed text-sm font-medium flex items-center justify-center gap-2 transition-colors"
            style={{ borderColor: '#D1D5DB', color: '#6B7280' }}
          >
            <Plus size={16} />
            Add another dispatcher
          </button>

          <div
            style={{ backgroundColor: '#EFF6FF', borderColor: '#BFDBFE' }}
            className="border rounded-lg p-3"
          >
            <p style={{ color: '#1E40AF' }} className="text-xs">
              <strong>Note:</strong> Invitees accept through the Relay admin web
              app using the link in their email — this is separate from the
              Driver mobile app.
            </p>
          </div>
        </div>

        <div
          className="border-t px-4 pb-10 pt-4 space-y-3"
          style={{ borderColor: '#E5E7EB' }}
        >
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-opacity"
            style={{
              backgroundColor: '#172F27',
              opacity: isSubmitting ? 0.5 : 1,
            }}
          >
            {isSubmitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Sending…
              </>
            ) : (
              <>
                Continue
                <ArrowRight size={18} />
              </>
            )}
          </button>

          <div className="flex justify-center">
            <button
              type="button"
              onClick={onSkip}
              className="text-sm font-medium"
              style={{ color: '#6B7280' }}
            >
              Skip for now
            </button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default InviteDispatchersOnboardingScreen;
